import dayjs from "dayjs";
import { HydratedMetric } from "../HydratedMetric";
import { WindowDelta } from "./WindowDelta";
import { YTDDelta } from "./YTDDelta";
import { DeltaType, MetricType, PeriodOption } from "../Metrics.types";
import { DeltaFormula } from "./DeltaBase";
import { HydratedValue } from "../HydratedValue";

export { DeltaBase, DeltaResult, DeltaFormula } from "./DeltaBase";
export { WindowDelta } from "./WindowDelta";
export { YTDDelta } from "./YTDDelta";

export type Delta = {
    formula: DeltaFormula | null;
    result: HydratedValue;
    errorMessage: string | null;
    metric: HydratedMetric | null;
};

export class DeltaCalculator {
    readonly metric: HydratedMetric;
    private asOfDate: dayjs.Dayjs;

    constructor(metric: HydratedMetric, asOfDate: dayjs.Dayjs) {
        this.metric = metric;
        this.asOfDate = asOfDate;
    }

    getWindowDelta(sliceSize: number): WindowDelta {
        return new WindowDelta(this.metric, this.asOfDate, sliceSize);
    }

    getYTDDelta(): YTDDelta {
        return new YTDDelta(this.metric, this.asOfDate);
    }

    static getEmptyDelta(): Delta {
        return {
            metric: null,
            formula: null,
            result: new HydratedValue(null, MetricType.PERCENTAGE),
            errorMessage:
                "Internal error: the Delta Calculator was not given a metric to calculate.",
        };
    }

    getDelta(deltaType: DeltaType, periodOption: PeriodOption): Delta {
        const isYTD = periodOption.id === "ytd";
        const isAll = periodOption.id === "all";

        if (deltaType === DeltaType.CUMULATIVE) {
            if (isAll) {
                return {
                    metric: this.metric,
                    formula: null,
                    result: new HydratedValue(null, this.metric.type),
                    errorMessage:
                        'Cumulative deltas cannot be calculated when "All Available Data" is selected.',
                };
            } else if (isYTD) {
                const d = this.getYTDDelta();
                return {
                    metric: this.metric,
                    formula: d.getDeltaFormula(),
                    result: d.result.value,
                    errorMessage: d.getErrorMessage(),
                };
            } else if (periodOption.threshold) {
                const d = this.getWindowDelta(periodOption.threshold);
                return {
                    metric: this.metric,
                    formula: d.getDeltaFormula(),
                    result: d.result.value,
                    errorMessage: d.getErrorMessage(),
                };
            }
        } else if (deltaType === DeltaType.PERIOD_OVER_PERIOD) {
            const d = this.getWindowDelta(1);
            return {
                metric: this.metric,
                formula: d.getDeltaFormula(),
                result: d.result.value,
                errorMessage: d.getErrorMessage(),
            };
        }

        return {
            metric: this.metric,
            formula: null,
            result: new HydratedValue(null, this.metric.type),
            errorMessage:
                "Delta cannot be calculated from your period selection.",
        };
    }
}
